import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps, navigate, useLocation } from '@reach/router';
import { parse } from 'qs';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { callGetDiagnosticsReport } from '../../../../particles/api/extension';
import { routes } from '../../../../particles/common';
import { reportFindings } from './particles/findings';
import { ChatSnapshot, Errors, Findings, Overview, PageState, SyncHistory, Trail } from './particles/sections';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3)
}));

/**
 * Look up a report by the code the customer was shown ("SR-7F3K2Q").
 *
 * The code lives in the URL (`?code=`), so a lookup can be pasted into a
 * ticket or Slack thread and opens on the same report. Reports never change
 * after they're sent, hence `staleTime: Infinity`.
 */
const SADiagnosticsLookup: React.FC<RouteComponentProps> = () => {
  const location = useLocation();
  const code = String(parse(location.search, { ignoreQueryPrefix: true }).code ?? '').trim();
  const [input, setInput] = useState(code);

  useEffect(() => setInput(code), [code]);

  const query = useQuery({
    queryKey: ['extension-report', code.toUpperCase()],
    queryFn: () => callGetDiagnosticsReport(code),
    enabled: code.length > 0,
    staleTime: Infinity,
    retry: false
  });

  const report = query.data?.code === 200 ? query.data.data : undefined;
  const findings = useMemo(() => (report ? reportFindings(report) : []), [report]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (next) navigate(`${routes.SA_DiagnosticsLookup}?code=${encodeURIComponent(next)}`);
  };

  let notFound: string | null = null;
  if (query.data && query.data.code !== 200) {
    notFound =
      query.data.code === 404
        ? `No report with code ${code.toUpperCase()}. Check it with the customer — codes look like SR-7F3K2Q.`
        : query.data.error?.message || `The lookup failed (${query.data.code}).`;
  }

  return (
    <StyledContainer maxWidth="xl">
      <Stack gap={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Customers get a code from <strong>Help → Send diagnostics</strong> in the extension, or with a
            support ticket sent with diagnostics included.
          </Typography>
          <Stack component="form" direction="row" gap={1} onSubmit={submit}>
            <TextField
              size="small"
              label="Reference code"
              placeholder="SR-7F3K2Q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
              sx={{ width: 240 }}
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={!input.trim() || query.isFetching}>
              Look up
            </Button>
            {query.isFetching && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
          </Stack>
        </Paper>

        {notFound && <Alert severity="warning">{notFound}</Alert>}

        {report && (
          <>
            <Overview report={report} />
            <Findings findings={findings} />
            <PageState report={report} />
            <ChatSnapshot report={report} />
            <SyncHistory report={report} />
            <Errors report={report} />
            <Trail report={report} />
          </>
        )}
      </Stack>
    </StyledContainer>
  );
};

export default SADiagnosticsLookup;
